import { useEffect, useRef } from "react";

import { useEnhancementPolling } from "@design-lab/shared/hooks/useEnhancementPolling";
import { trackDesignLabEnhancementFailed } from "@design-lab/shared/lib/analytics";
import { useNotification } from "@design-lab/shared/ui/notification";

import type { Artwork } from "../types";
import { useArtwork } from "./useArtwork";
import { useImagesWarmFastCache } from "./useImagesFastCache";

interface UseArtworkWithPollingOptions {
  artworkId?: number | undefined;
  startTime?: number | undefined;
  machine: any;
  enabled?: boolean;
}

export function useArtworkWithPolling({
  artworkId,
  startTime,
  machine,
  enabled = true
}: UseArtworkWithPollingOptions) {
  const hasStartedPolling = useRef(false);
  const hasCompletedEnhancement = useRef(false);
  const { warmCache } = useImagesWarmFastCache();
  const { getArtwork } = useArtwork();
  const { error: notifyError } = useNotification();

  const { startPolling, stopPolling, isPolling } = useEnhancementPolling({
    artworkId: artworkId || 0,
    startTime: startTime || Date.now(),
    onEnhancementComplete: async (artwork: Artwork) => {
      if (!hasCompletedEnhancement.current) {
        hasCompletedEnhancement.current = true;

        try {
          if (machine.state.matches("processing.uploaded")) {
            machine.send({ type: "enhancement.started" });
          }

          machine.send({ type: "cache.warming.started" });

          const imageUrl = artwork.noBgUrl || artwork.noBgNoTrimUrl || artwork.withBgUrl;
          if (imageUrl) {
            await warmCache([imageUrl]);
          }

          machine.send({ type: "cache.warmed" });
          machine.send({ type: "enhancement.completed", artwork });
        } catch (error) {
          console.error(`[ArtworkPolling] Error during enhancement completion:`, error);
          window.Honeybadger?.notify("Artwork polling error during enhancement completion", {
            params: {
              error: error instanceof Error ? error.message : String(error),
              error_object: error instanceof Error ? error.toString() : String(error),
              stack_trace: error instanceof Error ? error.stack : null,
              hook: "useArtworkWithPolling",
              artworkId
            }
          });
        }
      }
    },
    onEnhancementFailed: async (errorMessage: string) => {
      // Track the failure analytics
      trackDesignLabEnhancementFailed({
        artworkId: String(artworkId),
        errorMessage
      });

      // Reset the machine to initial state (same as upload failure)
      machine.send({
        type: "reset",
        targetState: "initial",
        context: {
          artworkId,
          visitedStates: [],
          cacheWarmingStatus: "none"
        }
      });

      notifyError({
        title: "Enhancement Failed",
        description: errorMessage
      });
    },
    enabled: enabled && !!artworkId && !!startTime,
    isArtworkPresent: !!artworkId
  });

  useEffect(() => {
    hasStartedPolling.current = false;
    hasCompletedEnhancement.current = false;
  }, [artworkId]);

  useEffect(() => {
    if (!artworkId || machine.state.matches("finished") || machine.state.matches("initial")) {
      return;
    }

    if (
      enabled &&
      startTime &&
      !hasStartedPolling.current &&
      (machine.state.matches("processing.uploaded") ||
        machine.state.matches("processing.enhancing"))
    ) {
      hasStartedPolling.current = true;
      startPolling();
    }
  }, [enabled, artworkId, startTime, machine.state, startPolling]);

  useEffect(() => {
    if (!artworkId) return;

    if (machine.state.matches("finished") && isPolling) {
      stopPolling();
    }
  }, [machine.state, isPolling, stopPolling, artworkId, getArtwork]);

  useEffect(() => {
    if (!artworkId) return;

    if (machine.state.matches("initial")) {
      hasStartedPolling.current = false;
      hasCompletedEnhancement.current = false;
      stopPolling();
    }
  }, [machine.state, stopPolling, artworkId]);

  return {
    isPolling,
    stopPolling
  };
}
